import { Injectable } from "@angular/core";

import { Observable, throwError, zip } from "rxjs";
import { map, catchError, tap, finalize } from "rxjs/operators";

import { User, UserWarning } from "@app/core/domain";
import { UserApiClient } from "@app/core/data-services";
import { UserStateBase } from "./user-state.interface";
import { AuthService } from "./auth";

export interface UserFacadeInterface {
  getCurrentUser(): Observable<User>;
  getCurrentUserSnapshot(): User;
  fetchCurrentUser(): Observable<User>;
  currentUserHasPlcCard(): Observable<boolean>;
  isCurrentUserLoading(): Observable<boolean>;
  hasDateOfBirth(): Observable<boolean>;
  ageVerifiedNegative(): Observable<boolean>;
  ageOK(): Observable<boolean>;
  hasMarkedMarketingConsent(): Observable<boolean>;
  setCurrentUserHasPlcCard(hasPlc: boolean): void;
  hasMultiplePlcAccounts(): Observable<boolean>;
  setMultiplePlcAccounts(multipleAccounts: boolean): void;
  hasMultiplePlcCardGroups(): Observable<boolean>;
  setMultiplePlcCardGroups(multipleCardGroups: boolean): void;
}

@Injectable()
export class UserFacade implements UserFacadeInterface {
  constructor(
    private state: UserStateBase,
    private api: UserApiClient,
    private auth: AuthService
  ) {}

  getCurrentUser(): Observable<User> {
    return this.state.getUser();
  }

  getCurrentUserSnapshot(): User {
    return this.state.getUserSnapshot();
  }

  fetchCurrentUser(): Observable<User> {
    this.state.setLoading();
    return this.api.getCurrentUser().pipe(
      tap((user: User) => {
        this.state.setState(user);
      }),
      tap((user: User) => this.verifyAge(user)),
      tap((user: User) => this.verifyMarketingConsent(user)),
      catchError((err) => {
        this.state.setLoading(false);
        return throwError(err);
      })
    );
  }

  private verifyMarketingConsent(user: User): void {
    if (UserWarning.MARKETING_CONSENT === user.warning) {
      this.state.setMarkedMarketingConsent(false);
    }
  }

  private verifyAge(user: User): void {
    if (user.warning === UserWarning.EMPTY_BIRTHDAY) {
      this.state.setDateOfBirthStatus(false);
      this.state.setAgeVerifiedNegative(true);
    } else if (user.warning === UserWarning.TOO_YOUNG) {
      this.state.setDateOfBirthStatus(true);
      this.state.setAgeVerifiedNegative(true);
    } else {
      this.state.setDateOfBirthStatus(true);
      this.state.setAgeVerifiedNegative(false);
    }
  }

  hasDateOfBirth(): Observable<boolean> {
    return this.state.hasDateOfBirth();
  }

  ageVerifiedNegative(): Observable<boolean> {
    return this.state.ageVerifiedNegative();
  }

  hasMarkedMarketingConsent(): Observable<boolean> {
    return this.state.hasMarkedMarketingConsent();
  }

  ageOK(): Observable<boolean> {
    return zip(this.hasDateOfBirth(), this.ageVerifiedNegative()).pipe(
      map((res) => {
        const [hasDOB, verifiedNegative] = res;
        return hasDOB && !verifiedNegative;
      })
    );
  }

  currentUserHasPlcCard(): Observable<boolean> {
    return this.state.userHasPlcCard();
  }

  setCurrentUserHasPlcCard(hasPlc: boolean): void {
    this.state.setUserHasPlcCard(hasPlc);
  }

  hasMultiplePlcAccounts(): Observable<boolean> {
    return this.state.userHasMultiplePlcAccounts();
  }

  setMultiplePlcAccounts(multipleAccounts: boolean): void {
    this.state.setUserHasMultiplePlcAccounts(multipleAccounts);
  }

  hasMultiplePlcCardGroups(): Observable<boolean> {
    return this.state.userHasMultipleCardGroups();
  }

  setMultiplePlcCardGroups(multipleCardGroups: boolean): void {
    this.state.setUserHasMultipleCardGroups(multipleCardGroups);
  }
  isCurrentUserLoading(): Observable<boolean> {
    return this.state.isLoading();
  }

  logout(): void {
    this.api.logout()
      .pipe(finalize(() => this.auth.logout()))
      .subscribe({ error: () => {} });
  }
}
